import React, { useReducer } from 'react';
import {
  isSameDay,
  startOfYear,
  startOfMonth,
  subYears,
  subMonths,
  format,
} from 'date-fns';
import AutoSizer from 'react-virtualized-auto-sizer';

import keyValueReducer from '@chronos/ui-toolkit/reducers/keyValueReducer';
import { setState } from '@chronos/ui-toolkit/actions';
import {
  useGetWorklogStatsQuery,
  useGetAccountStatsQuery,
  useGetTransactionStatsQuery,
} from 'slices/statsApi';

import {
  LineChart,
  BarChart,
  Bar,
  Line,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from 'recharts';
import Button from '@chronos/components/Button';
import DateRangePicker from '@chronos/components/DateRangePicker';

import * as S from './styled';

const now = new Date();
const startOfThisYear = startOfYear(now);
const startOfPreviousYear = subYears(startOfThisYear, 1);
const startOfThisMonth = startOfMonth(now);

const worklogCharts = [
  'worklogStatsByMonth',
  'worklogStatsByWeek',
  'worklogStatsByDay',
];

const transactionCharts = [
  'transactionStatsByMonth',
  'transactionStatsByWeek',
  'transactionStatsByDay',
];

const staticRangeHandler = {
  range: {},
  isSelected(range) {
    const definedRange = this.range();
    return (
      isSameDay(range.startDate, definedRange.startDate) &&
      isSameDay(range.endDate, definedRange.endDate)
    );
  },
};

function createStaticRanges(ranges) {
  return ranges.map((range) => ({ ...staticRangeHandler, ...range }));
}

const staticRanges = createStaticRanges([
  {
    range: () => ({
      startDate: startOfThisMonth,
      endDate: now,
    }),
    label: 'This month',
  },
  {
    range: () => ({
      startDate: subMonths(startOfThisMonth, 2),
      endDate: now,
    }),
    label: 'Last 3 months',
  },
  {
    range: () => ({
      startDate: subMonths(startOfThisMonth, 5),
      endDate: now,
    }),
    label: 'Last 6 months',
  },
  {
    range: () => ({
      startDate: startOfThisYear,
      endDate: now,
    }),
    label: 'This year',
  },
  {
    range: () => ({
      startDate: startOfPreviousYear,
      endDate: now,
    }),
    label: 'Last 2 years',
  },
  {
    range: () => ({
      startDate: subYears(startOfThisYear, 2),
      endDate: now,
    }),
    label: 'Last 3 years',
  },
  {
    range: () => ({
      startDate: subYears(startOfThisYear, 4),
      endDate: now,
    }),
    label: 'Last 5 years',
  },
]);

const predictRanges = [
  'currentMonth',
  'nextThreeMonths',
  'nextSixMonths',
  'currentYear',
  'nextTwelveMonths',
  'total',
];
const pricePredictRanges = ['threeMonths', 'sixMonths', 'twelveMonths'];

/* eslint-disable */
const PredictTooltip = ({ active, payload, label, labelFormatter }) => {
  if (active && payload && payload.length) {
    const { predict } = payload[0].payload;
    return (
      <div style={{ background: 'white', padding: '20px' }}>
        <div>{labelFormatter(null, payload)}</div>
        {payload.map(({ name, value, color }) => (
          <div
            key={name}
            style={{ color }}
          >
            {name}: {value}
          </div>
        ))}
        <div>Predict 📈</div>
        <S.PredictStats>
          {predictRanges.map((predictKey) => (
            <S.PredictStatItem key={predictKey}>
              {predictKey}:{' '}
              {pricePredictRanges
                .map((priceKey) => predict[predictKey][priceKey])
                .join(', ')}{' '}
              EUR ‖ ⏳ {predict[predictKey].expiredTrialWorkspaces}
            </S.PredictStatItem>
          ))}
        </S.PredictStats>
      </div>
    );
  }
  return null;
};
/* eslint-enable */

const initialState = {
  ranges: [
    {
      startDate: startOfPreviousYear,
      endDate: now,
      key: 'selection',
    },
  ],
};

function StatsPageContainer() {
  const [state, internalDispatch] = useReducer(keyValueReducer, initialState);

  const { data: worklogStats, isSuccess: isWorklogsStatsLoaded } =
    useGetWorklogStatsQuery({
      dateFrom: state.ranges[0].startDate.toISOString(),
      dateTo: state.ranges[0].endDate.toISOString(),
    });

  const { data: transactionStats, isSuccess: isTransactionStatsLoaded } =
    useGetTransactionStatsQuery({
      dateFrom: state.ranges[0].startDate.toISOString(),
      dateTo: state.ranges[0].endDate.toISOString(),
    });

  const { data: accountStats, isSuccess: isAccountStatsLoaded } =
    useGetAccountStatsQuery({
      dateFrom: state.ranges[0].startDate.toISOString(),
      dateTo: state.ranges[0].endDate.toISOString(),
    });

  if (
    !isWorklogsStatsLoaded ||
    !isTransactionStatsLoaded ||
    !isAccountStatsLoaded
  ) {
    return <div>Loading</div>;
  }

  return (
    <S.StatsPage>
      <S.Buttons>
        <DateRangePicker
          staticRanges={staticRanges}
          ranges={state.ranges}
          onChange={({ selection }) => {
            internalDispatch(
              setState({
                ranges: [selection],
              })
            );
          }}
        />
        {staticRanges.map(({ range, label }) => (
          <Button
            key={label}
            onClick={() => {
              internalDispatch(
                setState({
                  ranges: [
                    {
                      ...range(),
                      key: 'selection',
                    },
                  ],
                })
              );
            }}
          >
            {label}
          </Button>
        ))}
      </S.Buttons>
      <div>Current state 💶</div>
      <S.AccountStats>
        <S.AccountStatItem>
          Active Paid accounts: 💸 {accountStats.activePaidAccounts} ‖
        </S.AccountStatItem>
        <S.AccountStatItem>
          Active Paid workspaces: {accountStats.activePaidWorkspaces} ‖
        </S.AccountStatItem>
        <S.AccountStatItem>
          Active workspaces: {accountStats.activeWorkspaces} ‖
        </S.AccountStatItem>
        <S.AccountStatItem>
          Active Trial workspaces: ⏳ {accountStats.activeTrialWorkspaces} ‖
        </S.AccountStatItem>
      </S.AccountStats>
      <div>Predict 📈</div>
      <S.PredictStats>
        {predictRanges.map((predictKey) => (
          <S.PredictStatItem key={predictKey}>
            {predictKey}:{' '}
            {pricePredictRanges
              .map((priceKey) => accountStats.predict[predictKey][priceKey])
              .join(', ')}{' '}
            EUR ‖ ⏳ {accountStats.predict[predictKey].expiredTrialWorkspaces}
          </S.PredictStatItem>
        ))}
      </S.PredictStats>
      <S.Charts>
        <S.Chart>
          <S.ChartTitle>Account stats snap</S.ChartTitle>
          <AutoSizer>
            {({ height, width }) => (
              <div
                style={{
                  width,
                  height,
                }}
              >
                <BarChart
                  data={accountStats.snapsByDay}
                  width={width}
                  height={height}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 60,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="dateString" />
                  <YAxis />
                  <Tooltip
                    content={<PredictTooltip />}
                    labelFormatter={(value, items) => {
                      const date = items[0]?.payload?.date;
                      if (date) {
                        return format(new Date(date), 'Y - d MMM');
                      }
                      return 'Unknown';
                    }}
                  />
                  <Bar
                    dataKey="activePaidAccounts"
                    fill="#8884d8"
                  />
                  <Bar
                    dataKey="activePaidWorkspaces"
                    fill="#82ca9d"
                  />
                  <Bar
                    dataKey="activeWorkspaces"
                    fill="#3f51b5"
                  />
                </BarChart>
              </div>
            )}
          </AutoSizer>
        </S.Chart>
      </S.Charts>
      <S.Charts>
        {transactionCharts.map((chartKey) => (
          <S.Chart key={chartKey}>
            <S.ChartTitle>{chartKey}</S.ChartTitle>
            <AutoSizer>
              {({ height, width }) => (
                <div
                  style={{
                    width,
                    height,
                  }}
                >
                  <BarChart
                    data={transactionStats[chartKey]}
                    width={width}
                    height={height}
                    margin={{
                      top: 5,
                      right: 30,
                      left: 20,
                      bottom: 60,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dateString" />
                    <YAxis />
                    <Tooltip
                      labelFormatter={(value, items) => {
                        const date = items[0]?.payload?.date;
                        if (date) {
                          return format(new Date(date), 'Y - d MMM');
                        }
                        return 'Unknown';
                      }}
                    />
                    <Bar
                      dataKey="totalWithDiscount"
                      fill="#8884d8"
                    />
                    <Bar
                      dataKey="discount"
                      fill="#82ca9d"
                    />
                    <Bar
                      dataKey="transactions"
                      fill="#3f51b5"
                    />
                  </BarChart>
                </div>
              )}
            </AutoSizer>
          </S.Chart>
        ))}
      </S.Charts>
      <S.Charts>
        {transactionCharts.map((chartKey) => (
          <S.Chart key={chartKey}>
            <S.ChartTitle>{chartKey}</S.ChartTitle>
            <AutoSizer>
              {({ height, width }) => (
                <div
                  style={{
                    width,
                    height,
                  }}
                >
                  <BarChart
                    data={transactionStats[chartKey]}
                    width={width}
                    height={height}
                    margin={{
                      top: 5,
                      right: 30,
                      left: 20,
                      bottom: 60,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dateString" />
                    <YAxis />
                    <Tooltip
                      labelFormatter={(value, items) => {
                        const date = items[0]?.payload?.date;
                        if (date) {
                          return format(new Date(date), 'Y - d MMM');
                        }
                        return 'Unknown';
                      }}
                    />
                    <Bar
                      dataKey="accounts"
                      fill="#8884d8"
                    />
                    <Bar
                      dataKey="workspaces"
                      fill="#3f51b5"
                    />
                  </BarChart>
                </div>
              )}
            </AutoSizer>
          </S.Chart>
        ))}
      </S.Charts>
      <S.Charts>
        {worklogCharts.map((chartKey) => (
          <S.Chart key={chartKey}>
            <S.ChartTitle>{chartKey}</S.ChartTitle>
            <AutoSizer>
              {({ height, width }) => (
                <div
                  style={{
                    width,
                    height,
                  }}
                >
                  <LineChart
                    width={width}
                    height={height}
                    data={worklogStats[chartKey]}
                    margin={{
                      bottom: 40,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dateString" />
                    <YAxis />
                    <Tooltip
                      labelFormatter={(value, items) => {
                        const date = items[0]?.payload?.date;
                        if (date) {
                          return format(new Date(date), 'Y - d MMM');
                        }
                        return 'Unknown';
                      }}
                    />
                    <Legend />
                    <Line
                      dot={false}
                      type="step"
                      dataKey="accounts"
                      stroke="#8884d8"
                    />
                    <Line
                      dot={false}
                      type="step"
                      dataKey="accountsPaid"
                      stroke="#8884d8"
                    />
                    <Line
                      dot={false}
                      type="step"
                      dataKey="boards"
                      stroke="#82ca9d"
                    />
                    <Line
                      dot={false}
                      type="step"
                      dataKey="workspaces"
                      stroke="#10194a"
                    />
                    <Line
                      dot={false}
                      type="step"
                      dataKey="workspacesPaid"
                      stroke="#10194a"
                    />
                  </LineChart>
                </div>
              )}
            </AutoSizer>
          </S.Chart>
        ))}
      </S.Charts>
    </S.StatsPage>
  );
}

StatsPageContainer.propTypes = {};

export default StatsPageContainer;
